import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useUserProfile, useUpdateProfile } from "../../hooks/useAuth";
import logo from "../../assets/Revibelogo.png";

const EditProfileScreen = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    height: "",
    weight: "",
    gender: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const { data: profileData } = useUserProfile();
  const updateProfileMutation = useUpdateProfile();

  useEffect(() => {
    if (!token) navigate("/login");
  }, [token, navigate]);

  useEffect(() => {
    if (profileData) {
      const u = profileData.user || profileData;
      setFormData({
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        dob: u.dob ? new Date(u.dob).toISOString().split("T")[0] : "",
        height: u.height?.toString() || "",
        weight: u.weight?.toString() || "",
        gender: u.gender || "",
      });
    }
  }, [profileData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess("");
  };

  const handleSubmit = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError("First and last name are required.");
      return;
    }
    try {
      const result = await updateProfileMutation.mutateAsync({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        dob: formData.dob,
        height: parseInt(formData.height),
        weight: parseInt(formData.weight),
        gender: formData.gender,
      });
      const savedUser = result.user || { ...formData };
      const current = JSON.parse(localStorage.getItem("userData") || "{}");
      localStorage.setItem(
        "userData",
        JSON.stringify({ ...current, ...savedUser }),
      );
      window.dispatchEvent(new Event("userUpdated"));
      setSuccess("Profile updated successfully!");
      setTimeout(() => navigate(-1), 1200);
    } catch (err) {
      setError(
        err.response?.data?.message || "Network error. Please try again.",
      );
    }
  };

  const inputClass =
    "w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-4 text-gray-900 text-base font-semibold outline-none focus:border-[#c6f135] focus:ring-2 focus:ring-[#c6f135]/20 transition-all placeholder-gray-400";

  const genderOptions = ["Male", "Female", "Other"];

  return (
   <div className="bg-white text-gray-900 flex flex-col container mx-auto px-4 pb-10">
      {/* Back Button */}
      <div className="pt-4 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="bg-transparent border-0 text-gray-700 cursor-pointer p-0 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
      </div>

      {/* Logo + Title */}
      <div className="flex flex-col items-center justify-center mb-10 text-center">
        {/* Logo */}
        <Link to="/" className="mb-4">
          <img
            src={logo}
            alt="Revibe logo"
            className="h-10 w-auto object-contain"
          />
        </Link>

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          Profile
        </h1>

        {/* Subtitle */}
        <p className="text-gray-500 text-sm mt-1">Edit your profile details</p>
      </div>
      {/* Error / Success */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm text-center">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-[#c6f135]/10 border border-[#c6f135]/30 rounded-xl px-4 py-3 text-green-600 text-sm text-center">
          {success}
        </div>
      )}

      {/* Name Row */}
      <div className="flex gap-3 mb-4">
        <input
          type="text"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          placeholder="First Name"
          className={`${inputClass} flex-1`}
        />
        <input
          type="text"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          placeholder="Last Name"
          className={`${inputClass} flex-1`}
        />
      </div>

      {/* DOB */}
      <div className="relative mb-4">
        <input
          type="date"
          name="dob"
          value={formData.dob}
          onChange={handleChange}
          className={`${inputClass} w-full`}
        />
      </div>

      {/* Height + Weight */}
      <div className="flex gap-3 mb-6">
        <input
          type="number"
          name="height"
          value={formData.height}
          onChange={handleChange}
          placeholder="Height (cm)"
          className={`${inputClass} flex-1`}
        />
        <input
          type="number"
          name="weight"
          value={formData.weight}
          onChange={handleChange}
          placeholder="Weight (kg)"
          className={`${inputClass} flex-1`}
        />
      </div>

      {/* Gender */}
      <div className="mb-8">
        <p className="text-gray-600 text-sm mb-3">Gender</p>
        <div className="flex gap-3">
          {genderOptions.map((g) => (
            <button
              key={g}
              onClick={() => setFormData((prev) => ({ ...prev, gender: g }))}
              className={`flex-1 py-3 rounded-xl text-sm font-semibold border-0 cursor-pointer transition-all ${
                formData.gender === g
                  ? "bg-[#c6f135] text-black shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {formData.gender === g && <span className="mr-1">✓</span>}
              {g}
            </button>
          ))}
        </div>
      </div>

   {/* Submit */}
<div className="flex justify-center">
  <button
    onClick={handleSubmit}
    disabled={updateProfileMutation.isPending}
    className="bg-[#c6f135] text-black font-semibold text-sm px-8 py-4 rounded-full border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:bg-[#b8e030] transition-colors shadow-sm"
  >
    {updateProfileMutation.isPending ? (
      <>
        <Loader2 className="animate-spin" size={16} />
        Updating...
      </>
    ) : (
      "Update Profile"
    )}
  </button>
</div>
    </div>
  );
};

export default EditProfileScreen;
